import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';

const EnhancedHero = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  useEffect(() => {
    const fadeElems = document.querySelectorAll('.fade-in');
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    fadeElems.forEach(elem => observer.observe(elem));
    
    return () => {
      fadeElems.forEach(elem => observer.unobserve(elem));
    };
  }, []);

  // SVG industrial background for hero section
  const industrialSvg = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600' fill='none'%3E%3Cpath d='M0 500H800' stroke='%23444' stroke-opacity='0.2' stroke-width='2'/%3E%3Cpath d='M100 500V400H150V500' stroke='%23444' stroke-opacity='0.2' stroke-width='2'/%3E%3Cpath d='M200 500V350H250V500' stroke='%23444' stroke-opacity='0.2' stroke-width='2'/%3E%3Cpath d='M300 500V300H350V500' stroke='%23444' stroke-opacity='0.2' stroke-width='2'/%3E%3Cpath d='M400 500V250H450V500' stroke='%23444' stroke-opacity='0.2' stroke-width='2'/%3E%3Cpath d='M500 500V350H550V500' stroke='%23444' stroke-opacity='0.2' stroke-width='2'/%3E%3Cpath d='M600 500V400H650V500' stroke='%23444' stroke-opacity='0.2' stroke-width='2'/%3E%3Ccircle cx='125' cy='450' r='10' fill='%23FF6B00' fill-opacity='0.3'/%3E%3Ccircle cx='225' cy='400' r='10' fill='%23FF6B00' fill-opacity='0.3'/%3E%3Ccircle cx='325' cy='350' r='10' fill='%23FF6B00' fill-opacity='0.3'/%3E%3Ccircle cx='425' cy='300' r='10' fill='%23FF6B00' fill-opacity='0.3'/%3E%3Ccircle cx='525' cy='400' r='10' fill='%23FF6B00' fill-opacity='0.3'/%3E%3Ccircle cx='625' cy='450' r='10' fill='%23FF6B00' fill-opacity='0.3'/%3E%3C/svg%3E`;

  return (
    <section className="hero-enhanced mobile-friendly-hero">
      {/* Background Decorations */}
      <div className="blob" style={{ top: '-20%', right: '-10%' }}></div>
      <div className="blob" style={{ bottom: '-30%', left: '-15%' }}></div>
      <div className="bg-pattern"></div>
      
      {/* Industrial SVG Background */}
      <div className="industrial-bg" style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '60%',
        opacity: 0.15,
        backgroundImage: `url(${industrialSvg})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'bottom center',
        backgroundSize: 'cover',
        zIndex: 1
      }}></div>
      
      <div className="hero-content-enhanced container">
        <div className="grid" style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)', 
          gap: '2rem', 
          alignItems: 'center', 
          minHeight: '70vh'
        }}>
          <div className="col-span-7 md-col-span-12" style={{
            gridColumn: 'span 7'
          }}>
            <div style={{ maxWidth: '800px' }}>
              <h1 className="hero-title fade-in" style={{ animationDelay: '0.2s', fontSize: '3rem' }}>
                {language === 'fr' 
                  ? 'Excellence en solutions de mesure dans le secteur Pétrole & Gaz et Industrie'
                  : language === 'ar' 
                  ? 'التميز في حلول القياس في قطاع النفط والغاز والصناعة'
                  : 'Excellence in measurement solutions for Oil, Gas and Industry'}
              </h1>
              <p className="hero-subtitle fade-in" style={{ animationDelay: '0.4s', fontSize: '1.25rem', marginBottom: '2rem' }}>
                {language === 'fr' 
                  ? 'Plus de 20 ans d\'expertise combinée en instrumentation Panametrics'
                  : language === 'ar' 
                  ? 'أكثر من 20 عامًا من الخبرة المشتركة في أجهزة Panametrics'
                  : '20+ Years of Combined Expertise in Panametrics Instrumentation'}
              </p>
              
              {/* Added trust indicators */}
              <div className="trust-indicators fade-in" style={{ 
                display: 'flex', 
                gap: '1.5rem', 
                marginBottom: '2rem', 
                animationDelay: '0.5s',
                flexWrap: 'wrap'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  padding: '0.5rem 1rem',
                  borderRadius: '2rem'
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '20px', height: '20px', marginRight: '0.5rem', color: 'var(--assco-orange)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem', fontWeight: '500' }}>
                    {language === 'fr' ? 'Expertise Certifiée' : 
                     language === 'ar' ? 'خبرة معتمدة' : 
                     'Certified Expertise'}
                  </span>
                </div>

                
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  padding: '0.5rem 1rem',
                  borderRadius: '2rem'
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '20px', height: '20px', marginRight: '0.5rem', color: 'var(--assco-orange)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem', fontWeight: '500' }}>
                    {language === 'fr' ? 'Support 24/7' : 
                     language === 'ar' ? 'دعم على مدار الساعة' : 
                     '24/7 Support'}
                  </span>
                </div>
              </div>
              
              <div className="hero-cta-group fade-in" style={{ animationDelay: '0.6s' }}>
                <Link to="/contact" className="btn btn-primary btn-icon">
                  {language === 'fr' ? 'Demander un devis' : 
                   language === 'ar' ? 'طلب عرض أسعار' : 
                   'Get Quote'}
                  <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '20px', height: '20px', marginLeft: '0.5rem' }} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
                <Link to="/services" className="btn btn-outline">
                  {language === 'fr' ? 'Nos services' : 
                   language === 'ar' ? 'خدماتنا' : 
                   'Our Services'}
                </Link>
              </div>
            </div>
          </div>
          
          <div className="col-span-5 md-col-span-12" style={{
            gridColumn: 'span 5'
          }}>
            {/* 3D Visualization of Industrial Equipment */}
            <div className="hero-visual fade-in" style={{ 
              animationDelay: '0.7s',
              position: 'relative',
              height: '100%',
              minHeight: '500px',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              padding: '2rem',
              paddingTop: '2rem'
            }}>
              <div style={{
                position: 'relative',
                width: '100%',
                maxWidth: '400px',
                height: '400px',
                perspective: '1000px',
                margin: '0'
              }}>
                                {/* Simple Logo with Text */}
                <div style={{
                  position: 'absolute',
                  top: '15%',
                  left: '40%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'float 8s ease-in-out infinite'
                }}>
                  {/* ASSCO Logo */}
                  <div style={{
                    width: '160px',
                    height: '160px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(255,107,0,0.9) 0%, rgba(255,140,0,0.9) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 40px rgba(255, 107, 0, 0.6), 0 20px 45px rgba(0, 0, 0, 0.4)',
                    marginBottom: '2.5rem',
                    position: 'relative',
                    border: '3px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <div style={{
                      width: '120px',
                      height: '120px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(0, 0, 0, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                      }}>
                        <img 
                          src="/images/assco-white.png" 
                          alt="ASSCO Logo" 
                          style={{ 
                          width: '80px',
                          height: 'auto',
                          filter: 'brightness(1.2)'
                          }} 
                        />
                    </div>
                  </div>
                  
                  {/* Text */}
                  <div style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '1.5rem',
                    textAlign: 'center',
                    textShadow: '0 2px 15px rgba(0,0,0,0.8)',
                    lineHeight: '1.3',
                    maxWidth: '320px'
                  }}>
                    {language === 'fr' ? 'Solutions de Mesure Avancées' : 
                     language === 'ar' ? 'حلول قياس متقدمة' : 
                     'Advanced Measurement Solutions'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Shape Divider - Ultra Smooth Mobile Wave */}
      <div className="shape-divider" style={{ transform: 'rotate(180deg)' }}>
        <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <defs>
            <filter id="smooth-home" colorInterpolationFilters="sRGB">
              <feGaussianBlur in="SourceGraphic" stdDeviation="0.5"/>
            </filter>
          </defs>
          <path d="M0,0V46.29c47.79,22.4,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" className="shape-fill" style={{filter: 'url(#smooth-home)'}}></path>
        </svg>
      </div>
      
      <style jsx>{`
        .hero-enhanced {
          position: relative;
          min-height: 100vh;
          display: flex;
          align-items: center;
          background-color: #1a1a1a;
          overflow: hidden;
        }
        
        .hero-content-enhanced {
          position: relative;
          z-index: 2;
          width: 100%;
          padding-top: 6rem;
          padding-bottom: 6rem;
        }
        
        @keyframes float {
          0% {
            transform: translate(-50%, -50%) rotateY(15deg) rotateX(10deg) translateY(0px);
          }
          50% {
            transform: translate(-50%, -50%) rotateY(15deg) rotateX(10deg) translateY(-20px);
          }
          100% {
            transform: translate(-50%, -50%) rotateY(15deg) rotateX(10deg) translateY(0px);
          }
        }
        
        @media (max-width: 768px) {
          .hero-content-enhanced {
            padding-top: 4rem;
            padding-bottom: 4rem;
          }
          
          .hero-visual {
            min-height: 400px !important;
            padding: 1rem !important;
            align-items: flex-start !important;
            justify-content: center !important;
            padding-top: 2rem !important;
          }
          
          .hero-visual > div {
            max-width: 320px !important;
            height: 320px !important;
          }
          
          .hero-visual div[style*="160px"] {
            width: 130px !important;
            height: 130px !important;
          }
          
          .hero-visual div[style*="120px"] {
            width: 100px !important;
            height: 100px !important;
          }
          
          .hero-visual img[style*="80px"] {
            width: 60px !important;
          }
          
          .hero-visual div[style*="1.5rem"] {
            font-size: 1.25rem !important;
            max-width: 280px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default EnhancedHero;